import { enqueueSnackbar, OptionsObject } from 'notistack';
import { createElement } from 'react';
import { TitledNotificationBody } from './TitledNotificationBody';
// Brings this package's notistack variant augmentation (notistack.d.ts) into the program of every
// app that compiles this file. Each app also declares the variants it uses in its own
// notistack-augmentation.d.ts, but an app cannot know about the titled variants until it is
// changed; without this import its type-check rejects the variants named below.
import type {} from './notistack';

type NotificationOptions = Partial<Omit<OptionsObject, 'variant'>>;

const defaultSnackbarProps: NotificationOptions = {
  anchorOrigin: {
    horizontal: 'right',
    vertical: 'top',
  },
  TransitionProps: {
    direction: 'down',
  },
};

type NotificationVariant = OptionsObject['variant'];

export const enqueSuccessNotification = (message: string) => {
  enqueNotificationWithOptions('successNotification', message, defaultSnackbarProps);
};

export const enqueErrorNotification = (message: string) => {
  enqueNotificationWithOptions('errorNotification', message, defaultSnackbarProps);
};

export const enqueNotificationWithOptions = (
  variant: NotificationVariant,
  message: string,
  options: NotificationOptions,
) => {
  enqueueSnackbar(message, {
    variant,
    ...defaultSnackbarProps,
    ...options,
  });
};

export type TitledNotificationSeverity = 'success' | 'error' | 'warning';

/** How many sentences the toast's text has. It decides how long the toast shows. */
export type TitledNotificationLength = 'one-sentence' | 'two-sentences';

// Design System timing: one sentence shows for 3 s, two sentences for 5 s.
const TITLED_NOTIFICATION_DURATION_MS: Record<TitledNotificationLength, number> = {
  'one-sentence': 3000,
  'two-sentences': 5000,
};

/**
 * Shows a filled toast with a title and a text (Figma `Product` node 1878:584). The caller states
 * the length of the text and the toast hides itself for that long. Needs a provider that registers
 * `titledNotificationComponents`: `NotificationProvider` does, and an app's own `SnackbarProvider`
 * spreads that map into its `Components`.
 */
export const enqueTitledNotification = (
  severity: TitledNotificationSeverity,
  content: { title: string; text: string; length: TitledNotificationLength },
) => {
  enqueueSnackbar(
    createElement(TitledNotificationBody, { title: content.title, text: content.text }),
    {
      variant: `${severity}TitledNotification`,
      ...defaultSnackbarProps,
      autoHideDuration: TITLED_NOTIFICATION_DURATION_MS[content.length],
    },
  );
};
